import { Permission } from '@frontend/authorization';
import { Dropdown, DropdownItem, DropdownItemProps } from '@frontend/components/common';
import { CommonMessage } from '@frontend/lang';
import { LuArrowLeft, LuEllipsisVertical } from 'react-icons/lu';
import { useNavigate } from 'react-router-dom';

export interface OverviewHeaderProps {
    title: React.ReactNode;
    onBack?: () => void;
    /**
     * When true, always show the back control. When omitted, the back control is shown only if
     * `onBack` is provided (nested pages). Main nav overview pages leave both unset.
     */
    showBack?: boolean;
    create?: {
        onCreate: () => void;
        entity: React.ReactNode;
        requiredPermissions?: Permission[];
        overwriteCreateLabel?: React.ReactNode;
        hide?: boolean;
    };
    /** When set, renders instead of the built-in `create` button (e.g. ApplicationCreateButton). */
    createButton?: React.ReactNode;
    components?: React.ReactNode;
    /**
     * Optional array of action nodes (buttons, links, etc) to render in the header.
     * Consumers are responsible for styling, permissions and behaviour of each action.
     */
    actions?: React.ReactNode[];
    optionsButtonProps?: {
        items?: DropdownItemProps[];
    };
}

const OverviewHeader = (props: OverviewHeaderProps) => {
    const navigate = useNavigate();
    const showBack = props.showBack ?? props.onBack != null;

    return (
        <div className='inline-flex w-full items-center justify-between'>
            <div className='flex items-center justify-start gap-3'>
                {showBack && (
                    <div
                        onClick={() => {
                            if (props.onBack) {
                                props.onBack();
                            } else {
                                navigate(-1);
                            }
                        }}
                        className='bg-background-surface border-border-default hover:bg-brand-light focus-visible:outline-brand-accent relative hidden size-11 cursor-pointer items-center justify-center self-center rounded-md border focus-visible:outline-2 focus-visible:outline-offset-2 md:flex'>
                        <LuArrowLeft className='text-brand-muted size-6' />
                    </div>
                )}
                <div className='text-foreground-default justify-start text-xl font-bold tracking-[var(--tracking-heading)] md:text-3xl'>{props.title}</div>
            </div>
            <div className='inline-flex h-full items-center justify-start gap-3'>
                {props.actions &&
                    props.actions.length > 0 &&
                    props.actions.map((action, idx) => (
                        <div
                            key={idx}
                            className='h-full'>
                            {action}
                        </div>
                    ))}

                {props.optionsButtonProps && props.optionsButtonProps.items && props.optionsButtonProps.items.length > 0 && (
                    <Dropdown
                        label={(<LuEllipsisVertical className='text-brand-muted text-lg font-bold' />) as any}
                        buttonStyle='size-11 hover:bg-brand-light cursor-pointer bg-background-surface rounded-md border border-border-default flex justify-center items-center focus-visible:outline-brand-accent focus-visible:outline-2 focus-visible:outline-offset-2'
                        hideChevron>
                        {props.optionsButtonProps?.items?.map((item, index) => (
                            <DropdownItem
                                key={index}
                                {...item}
                            />
                        ))}
                    </Dropdown>
                )}
                {props.createButton}
                {props.create && !props.create.hide && !props.createButton && (
                    <div
                        className='bg-brand-primary text-background-surface hover:bg-brand-secondary focus-visible:outline-brand-accent inline-flex items-center justify-center gap-2 rounded-md px-4 py-2.5 text-base font-medium hover:cursor-pointer focus-visible:outline-2 focus-visible:outline-offset-2'
                        onClick={() => props.create!.onCreate()}>
                        {props.create.overwriteCreateLabel ? props.create.overwriteCreateLabel : CommonMessage.FORMS.CREATE_OBJECT(props.create.entity)}
                    </div>
                )}
                {props.components && props.components}
            </div>
        </div>
    );
};

export default OverviewHeader;
